import React from 'react';

import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';

import { useLocale } from '@adsk/alloy-react-helpers';

import { defaultSelectBoxRenderer } from '../layout/defaultRenderers';
import SelectBox from '../SelectBox';
/**
 * AsyncSelectBox, extends [SelectBox](selectbox) to add async data support
 */
class AsyncSelectBox extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      isLoadingValue: false,
      defaultValue: this.props.defaultValue,
      options: this.props.options,
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.loadOptionsDebounced = debounce(this.loadOptions, props.inputDebounce);

    this._mounted = false;
    this._cache = {};
  }

  componentDidMount() {
    this._mounted = true;
    const { options, autoLoad, loadDefaultValue } = this.props;

    if (loadDefaultValue) {
      this.loadDefaultValue();
    }

    if (options.length === 0 && autoLoad) {
      this.loadOptions('');
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    this._callback = null;
    this.loadOptionsDebounced.cancel();
  }

  loadDefaultValue() {
    const { loadDefaultValue } = this.props;

    const callback = (error, data) => {
      if (!this._mounted) return;

      this.setState({
        isLoadingValue: false,
        defaultValue: data,
      });
    };

    loadDefaultValue()
      .then((data) => callback(null, data))
      .catch((error) => callback(error));

    this.setState({
      isLoadingValue: true,
    });
  }

  loadOptions(inputValue) {
    const { loadOptions } = this.props;

    const callback = (error, data) => {
      if (!this._mounted) return;

      const options = Array.isArray(data) ? data : [];
      this._cache[inputValue] = options;

      if (callback === this._callback) {
        this._callback = null;

        this.setState({
          isLoading: false,
          options,
        });
      }
    };

    // Ignore all but the most recent request
    this._callback = callback;

    loadOptions(inputValue)
      .then((data) => callback(null, data))
      .catch((error) => callback(error));
  }

  getOptions(inputValue) {
    if (this._cache[inputValue]) {
      this._callback = null;

      this.setState({
        isLoading: false,
        options: this._cache[inputValue],
      });

      return;
    }

    if (!this.state.isLoading) {
      this.setState({
        isLoading: true,
      });
    }
    this.props.inputDebounce
      ? this.loadOptionsDebounced(inputValue)
      : this.loadOptions(inputValue);
  }

  handleInputChange(inputValue) {
    const { ignoreCase, onInputChange } = this.props;

    onInputChange && onInputChange(inputValue);
    this.getOptions(ignoreCase ? inputValue.toLowerCase() : inputValue);
  }

  handleCreate(inputValue, stateAndHelpers) {
    const { valueKey, labelKey, onCreate, ignoreCase } = this.props;

    const cache =
      this._cache[ignoreCase ? inputValue.toLowerCase() : inputValue];
    if (Array.isArray(cache)) {
      cache.unshift({
        [valueKey]: inputValue,
        [labelKey]: inputValue,
      });
    }

    onCreate(inputValue, stateAndHelpers);
  }

  handleOpen() {
    this.getOptions('');

    if (this.props?.onOpen) {
      this.props.onOpen();
    }
  }

  render() {
    const { children, placeholder, searchPlaceholder } = this.props;
    const {
      isLoading: _isLoading,
      isLoadingValue,
      options,
      defaultValue,
    } = this.state;
    const loadingPlaceholder =
      this.props.loadingPlaceholder || this.props['T'].translate('LOADING');

    const isLoading = _isLoading || isLoadingValue;

    return children({
      ...this.props,
      key: isLoadingValue,
      isLoading,
      defaultValue,
      onInputChange: this.handleInputChange,
      onCreate: this.handleCreate,
      onOpen: this.handleOpen,
      placeholder: isLoading ? loadingPlaceholder : placeholder,
      searchPlaceholder: isLoading ? loadingPlaceholder : searchPlaceholder,
      options: isLoading ? [] : options,
    });
  }
}

AsyncSelectBox.defaultProps = {
  ...SelectBox.defaultProps,
  children: defaultSelectBoxRenderer,
  inputDebounce: 250,
  autoLoad: true,
  ignoreCase: true,
  options: [],
  onCreate: () => {},
};

AsyncSelectBox.propTypes = {
  ...SelectBox.propTypes,
  /**
   * render function
   */
  children: PropTypes.func.isRequired,
  /**
   * input change debounce in millisecond
   */
  inputDebounce: PropTypes.number,
  /**
   * `(inputValue) => Promise(array)`, aysnc load options
   */
  loadOptions: PropTypes.func.isRequired,
  /**
   * `() => Promise(array | object)`, aysnc load defaultValue
   */
  loadDefaultValue: PropTypes.func,
  /**
   * placeholder when loading
   */
  loadingPlaceholder: PropTypes.node,
  /**
   * whether to load options on mount
   */
  autoLoad: PropTypes.bool,
  /**
   * whether to ignore case when loading options
   */
  ignoreCase: PropTypes.bool,
};

const WrapperAsyncSelectBox = (props) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  return <AsyncSelectBox {...props} T={T} />;
};

export default WrapperAsyncSelectBox;
